import { Lozenge } from "@/domains/common/components/lozenge"
import { CheckmarkCircleIcon, CloseCircleIcon } from "@/domains/icons"
import { cn } from "@/domains/library"

import type { TickSpacingOption } from "../../constants"
import type { PoolOption } from "../../stores/create-pool-store"

export interface TickSpacingSelectorViewProps {
  tickSpacings: Array<TickSpacingOption>
  selectedTickSpacing: number | undefined
  poolOptions: Array<PoolOption>
  onTickSpacingSelect: (tickSpacing: number) => void
}

/**
 * Pure View component for thick-v2 tick-spacing selection.
 */
export function TickSpacingSelectorView({
  tickSpacings,
  selectedTickSpacing,
  poolOptions,
  onTickSpacingSelect,
}: TickSpacingSelectorViewProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-sm font-medium text-t100">Tick Spacing</div>
      <div className="grid grid-cols-3 gap-2">
        {tickSpacings.map((spacing) => {
          const isSelected = spacing.key === selectedTickSpacing
          const poolOption = poolOptions.find((opt) => opt.tickSpacing.key === spacing.key)
          const poolExists = poolOption?.status === "existing" || poolOption?.status === "low-liquidity"
          const canCreate = poolOption?.status === "new"

          return (
            <button
              key={spacing.key}
              type="button"
              onClick={() => onTickSpacingSelect(spacing.key)}
              disabled={poolExists}
              className={cn(
                "group relative flex w-full flex-col items-start justify-center gap-1 rounded-lg border px-4 py-3 text-left transition-all",
                poolExists
                  ? "cursor-not-allowed border-p400-light bg-p400/50 opacity-60"
                  : isSelected
                    ? "border-p100 bg-p300-light shadow-sm hover:border-p100 hover:bg-p300-light"
                    : "border-p400-light bg-p400 hover:border-p100 hover:bg-p300-light",
              )}
            >
              {/* Status icon top right */}
              {canCreate && (
                <div className="absolute right-2 top-2">
                  <CheckmarkCircleIcon size={16} className="text-success-text" />
                </div>
              )}
              {poolExists && (
                <div className="absolute right-2 top-2">
                  <CloseCircleIcon size={16} className="text-error-text" />
                </div>
              )}

              <span className={cn("text-xs font-medium", isSelected ? "text-t100" : "text-t200")}>
                {spacing.description}
              </span>

              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-semibold", isSelected ? "text-t100" : "text-t200")}>
                  {spacing.value}
                </span>
              </div>

              <div className="flex w-full items-center justify-between">
                <span className={cn("text-xs", isSelected ? "text-t200" : "text-t300")}>
                  ticks per initialized step
                </span>
                {canCreate && (
                  <Lozenge color="success" size="xxs" className="absolute bottom-2 right-2">
                    ok
                  </Lozenge>
                )}
                {poolExists && (
                  <Lozenge color="error" size="xxs" className="absolute bottom-2 right-2">
                    exists
                  </Lozenge>
                )}
              </div>
              {poolOption?.rangePreview && (
                <div className="mt-2 w-full rounded-md border border-p300-light bg-p300 px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2 text-[11px] text-t200">
                    <span>
                      {poolOption.rangePreview.lowerTick} → {poolOption.rangePreview.upperTick}
                    </span>
                    <span>{poolOption.rangePreview.widthPct.toFixed(2)}%</span>
                  </div>
                  <div className="mt-1 truncate text-[11px] text-t300">
                    {poolOption.rangePreview.minPrice} - {poolOption.rangePreview.maxPrice}
                  </div>
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
